import { useState } from "react";
import Topbar from "../../components/Topbar";
import api, { downloadBlob, todayStr } from "../../lib/api";
import { toast } from "sonner";
import { FileArchive, Loader2, ShieldAlert } from "lucide-react";

// Fix WH89 (2026-10-28): descarga del código fuente vigente (ZIP). Con responseType "blob" el
// detalle de un error llega como Blob: se lee aquí para mostrar el mensaje real del servidor.
const detalleDe = async (e) => {
  try {
    const data = e?.response?.data;
    if (data instanceof Blob) return JSON.parse(await data.text())?.detail;
    return data?.detail;
  } catch {
    return null;
  }
};

const nombreDe = (res) => {
  const m = /filename="?([^";]+)"?/i.exec(res?.headers?.["content-disposition"] || "");
  return m ? m[1] : `chizy-chiz-codigo-fuente-${todayStr()}.zip`;
};

export default function CodigoFuente() {
  const [descargando, setDescargando] = useState(false);

  const descargar = async () => {
    setDescargando(true);
    try {
      const res = await api.post("/admin/source-export", null, { responseType: "blob" });
      downloadBlob(new Blob([res.data], { type: "application/zip" }), nombreDe(res));
      toast.success("ZIP del código fuente descargado");
    } catch (e) {
      const status = e?.response?.status;
      const detalle = await detalleDe(e);
      if (status === 429) toast.error(detalle || "Ya hay una exportación en curso. Inténtalo de nuevo en un minuto.");
      else toast.error(detalle || "No se pudo descargar el código fuente");
    } finally {
      setDescargando(false);
    }
  };

  return (
    <>
      <Topbar title="Código fuente" subtitle="Descarga del código vigente de la plataforma" />
      <div className="p-6 pb-28 space-y-5 max-w-3xl" data-testid="codigo-fuente-page">
        <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="codigo-fuente-aviso">
          <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
          <span>El ZIP contiene el código interno de la plataforma. Guárdalo en un lugar seguro y no lo compartas por canales públicos. Cada descarga queda registrada en la auditoría.</span>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="font-semibold text-slate-900 text-sm">ZIP completo del código vigente</div>
          <p className="text-xs text-slate-600">
            Incluye todo lo que hoy está en el proyecto, aunque todavía no esté confirmado en el repositorio: backend, frontend,
            scripts, migraciones, configuración, dependencias declaradas y archivos de bloqueo (lockfiles), assets y archivos ocultos
            necesarios. Trae un manifiesto (EXPORT_MANIFEST.json) con la ruta, el tamaño y el SHA256 de cada archivo, y la referencia de versión.
          </p>
          <p className="text-xs text-slate-600">
            No incluye: archivos .env ni claves, datos de clientes, base de datos ni respaldos, archivos subidos, logs, la carpeta .git,
            cachés ni dependencias que se reinstalan (node_modules). Los ejemplos de configuración salen sin valores.
          </p>
          <p className="text-xs text-slate-600">
            Un valor secreto en pruebas o documentación se reemplaza en el ZIP. Si aparece en el código de la aplicación, si hay un
            archivo que no se puede clasificar o si algo cambia mientras se genera, no se descarga nada y se muestra el motivo.
          </p>
          <button
            onClick={descargar}
            disabled={descargando}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
            data-testid="codigo-fuente-descargar"
          >
            {descargando ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileArchive className="w-4 h-4" />}
            {descargando ? "Generando ZIP..." : "Descargar código fuente (ZIP)"}
          </button>
        </div>
      </div>
    </>
  );
}
